import { gql } from '@apollo/client';

export const GET_COMPANY_BY_FRIENDLY_ID = gql`
  query GetCompanyByFriendlyId($friendlyCompanyId: String!) {
    companyByFriendlyId(friendlyCompanyId: $friendlyCompanyId) {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      managementRoles {
        id
        name
      }
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      orgTree
      createdAt
      updatedAt
    }
  }
`;

export const GET_CURRENT_COMPANY = gql`
  query GetCurrentCompany {
    currentCompany {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      managementRoles {
        id
        name
      }
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      orgTree
      createdAt
      updatedAt
    }
  }
`;

export const GET_COMPANY_BY_ID = gql`
  query GetCompanyById($id: ID!) {
    company(id: $id) {
      id
      friendlyCompanyId
      name
    }
  }
`;

export const UPDATE_CURRENT_COMPANY = gql`
  mutation UpdateCurrentCompany($input: UpdateCompanyInput!) {
    updateCurrentCompany(input: $input) {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      orgTree
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      managementRoles {
        id
        name
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_COMPANY = gql`
  mutation UpdateCompany($input: UpdateCompanyInput!, $id: ID!) {
    updateCompany(input: $input, id: $id) {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      orgTree
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      managementRoles {
        id
        name
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_COMPANY = gql`
  mutation DeleteCompany($id: ID!) {
    deleteCompany(id: $id)
  }
`;

export const CREATE_COMPANY = gql`
  mutation CreateCompany($input: CreateCompanyInput!) {
    createCompany(input: $input) {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      orgTree
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      managementRoles {
        id
        name
      }
    }
  }
`;

export const GET_COMPANIES_USER_BELONGS_TO = gql`
  query GetCompaniesUserBelongsTo {
    companiesUserBelongsTo {
      id
      friendlyCompanyId
      name
    }
  }
`;

export const GET_ADMIN_DASHBOARD_STATS = gql`
  query GetAdminDashboardStats {
    adminDashboardStats {
      totalActiveCompanies
      totalArchivedCompanies
      totalEmployees
      totalForms
      totalFormResponses
      companiesCreatedByMonth {
        month
        count
      }
      formResponsesByMonth {
        month
        count
      }
      companyStats {
        companyId
        companyName
        employeeCount
        formCount
        formResponseCount
        isArchived
        createdAt
      }
    }
  }
`;

export const GET_ALL_COMPANIES = gql`
  query GetAllCompanies {
    allCompanies {
      id
      friendlyCompanyId
      name
      ownerId
      orgTreeNextLevelKeys {
        id
        name
        color
      }
      managementRoles {
        id
        name
      }
      pointsOfContact {
        name
        email
        phoneNumber
        type
      }
      archivedDate
      featureSelections {
        id
        isEnabled
        extraProps
      }
      orgTree
      createdAt
      updatedAt
    }
  }
`;

/** Owner-only. There is one owner, so this gives the caller's own away. */
export const TRANSFER_COMPANY_OWNERSHIP = gql`
  mutation TransferCompanyOwnership($userId: ID!) {
    transferCompanyOwnership(userId: $userId) {
      id
      ownerId
    }
  }
`;

/** The owner may remove anyone but themselves; anyone may remove themselves. */
export const REMOVE_COMPANY_MEMBER = gql`
  mutation RemoveCompanyMember($userId: ID!) {
    removeCompanyMember(userId: $userId)
  }
`;

/** Owner-only. Outstanding invitations for the caller's company. */
export const GET_COMPANY_INVITATIONS = gql`
  query GetCompanyInvitations {
    companyInvitations {
      id
      email
      createdAt
      expiresAt
    }
  }
`;

/** Owner-only. Fails if already a member or already invited. */
export const SEND_COMPANY_INVITE = gql`
  mutation SendCompanyInvite($email: String!) {
    sendCompanyInvite(email: $email) {
      id
      email
      expiresAt
    }
  }
`;

export const CANCEL_COMPANY_INVITE = gql`
  mutation CancelCompanyInvite($id: ID!) {
    cancelCompanyInvite(id: $id) {
      id
    }
  }
`;

/**
 * Refused unless the signed-in address matches the one invited — the link is
 * a bearer credential, so forwarding it must not grant company access.
 */
export const ACCEPT_COMPANY_INVITE = gql`
  mutation AcceptCompanyInvite($token: String!) {
    acceptCompanyInvite(token: $token) {
      id
      name
      friendlyCompanyId
    }
  }
`;

/** Super admins only: one feature on or off for one company. */
export const SET_COMPANY_FEATURE = gql`
  mutation SetCompanyFeature(
    $companyId: ID!
    $feature: CompanyFeaturesEnum!
    $enabled: Boolean!
  ) {
    setCompanyFeature(companyId: $companyId, feature: $feature, enabled: $enabled) {
      id
      featureSelections {
        id
        isEnabled
        extraProps
      }
    }
  }
`;
